import type { Theme } from "@broadpaper/core";

/**
 * Document stylesheet shared by the editor canvas, the preview and the PDF
 * service. Everything is scoped under `.bp-doc` so it never leaks into a host
 * application. Block-specific visual styling is inline (resolved from the theme);
 * this sheet provides structure, print rules and design-mode affordances.
 */
export const DOCUMENT_CSS = `
.bp-doc, .bp-doc * { box-sizing: border-box; }
.bp-doc { -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility; font-variant-numeric: tabular-nums; }
.bp-doc p { margin: 0; }
.bp-doc h1, .bp-doc h2, .bp-doc h3, .bp-doc h4 { margin: 0; font-weight: inherit; }
.bp-doc img { max-width: 100%; }
.bp-doc a { color: inherit; }
.bp-doc table { border-spacing: 0; }
.bp-doc ul.bp-list, .bp-doc ol.bp-list { margin: 0; padding-left: 1.4em; }
.bp-doc .bp-list li { margin: 0.15em 0; }
.bp-doc .bp-code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.92em; background: rgba(0,0,0,0.05); padding: 0 3px; border-radius: 3px; }
.bp-doc .bp-p--empty { min-height: 1em; }

.bp-page { position: relative; overflow: hidden; background: #fff; color: #1a1d21; }
.bp-page__region { position: absolute; overflow: hidden; }
.bp-page__body { position: absolute; overflow: hidden; }
.bp-fragment { position: absolute; left: 0; width: 100%; overflow: hidden; }
.bp-fragment__clip { position: absolute; left: 0; right: 0; overflow: hidden; }
.bp-repeat-header { position: absolute; top: 0; overflow: hidden; }
.bp-repeat-header .bp-table { table-layout: fixed; }

.bp-stack { display: flex; flex-direction: column; }
.bp-stack > .bp-node { min-width: 0; }
.bp-node--hidden > * { opacity: 0.4; }
.bp-node--placeholder { opacity: 0.85; }
.bp-empty-section, .bp-empty-column { display: none; }
.bp-galley { position: absolute; left: -100000px; top: 0; visibility: hidden; pointer-events: none; }

.bp-table { width: 100%; border-collapse: collapse; }
.bp-table td, .bp-table th { word-wrap: break-word; overflow-wrap: anywhere; }
.bp-chart__svg text { user-select: none; }
.bp-page-break--print { height: 0; }

/* --- design mode affordances (only when .bp-doc--design) --- */
.bp-doc--design .bp-token { display: inline; padding: 0 5px; margin: 0 1px; border-radius: 4px; background: rgba(79, 111, 165, 0.14); color: #2f4f7f; font-size: 0.92em; font-weight: 500; white-space: nowrap; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
.bp-doc--design .bp-token--lg { font-size: 0.65em; vertical-align: middle; }
.bp-doc--design .bp-token__value { opacity: 0.65; margin-left: 4px; font-weight: 400; }
.bp-doc--design .bp-cell--bound { text-decoration: underline dotted rgba(79,111,165,0.45); text-underline-offset: 3px; text-decoration-thickness: 1px; }
.bp-doc--design .bp-placeholder-text { color: #9aa1ab; font-style: italic; }
.bp-doc--design .bp-empty-section { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; min-height: 96px; border: 1.5px dashed #cfd5dd; border-radius: 8px; color: #6b7280; font-size: 12px; font-family: Inter, system-ui, sans-serif; background: #fafbfc; }
.bp-doc--design .bp-empty-section__title { font-weight: 600; color: #4b5563; }
.bp-doc--design .bp-empty-column { display: flex; align-items: center; justify-content: center; min-height: 56px; border: 1.5px dashed #d4d9e0; border-radius: 6px; color: #9aa1ab; font-size: 11px; font-family: Inter, system-ui, sans-serif; }
.bp-doc--design .bp-spacer--design { background: repeating-linear-gradient(135deg, transparent 0 6px, rgba(0,0,0,0.04) 6px 8px); border-radius: 3px; }
.bp-doc--design .bp-page-break { display: flex; align-items: center; gap: 8px; margin: 2px 0; color: #b4541f; font-size: 9.5px; font-family: Inter, system-ui, sans-serif; text-transform: uppercase; letter-spacing: 0.06em; }
.bp-doc--design .bp-page-break::before, .bp-doc--design .bp-page-break::after { content: ""; flex: 1; border-top: 2px dashed rgba(180, 84, 31, 0.5); }
.bp-doc--design .bp-page-break__label { display: inline-flex; align-items: center; padding: 1px 8px; border-radius: 999px; font-weight: 600; background: rgba(180, 84, 31, 0.12); white-space: nowrap; }

/* Structural blocks draw nothing of their own, so design mode outlines them.
   The label is absolutely positioned: naming a box must not resize it. */
.bp-doc--design .bp-container,
.bp-doc--design .bp-keep-together { position: relative; outline: 1px dashed rgba(79, 111, 165, 0.32); outline-offset: 1px; border-radius: 3px; }
.bp-doc--design .bp-container::before,
.bp-doc--design .bp-keep-together::before {
  content: "Container"; position: absolute; top: -6px; left: 8px; z-index: 1; padding: 0 4px;
  background: #fff; color: #7c8697; font-family: Inter, system-ui, sans-serif; font-size: 8.5px;
  font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; pointer-events: none;
}
.bp-doc--design .bp-keep-together::before { content: "Keep together"; }
.bp-doc--design .bp-repeater__badge, .bp-doc--design .bp-conditional__badge, .bp-doc--design .bp-table__badge, .bp-doc--design .bp-chart__badge { display: inline-flex; align-items: center; gap: 4px; margin-bottom: 4px; padding: 1px 7px; border-radius: 999px; font-size: 9.5px; font-weight: 600; letter-spacing: 0.02em; font-family: Inter, system-ui, sans-serif; background: rgba(79,111,165,0.12); color: #2f4f7f; user-select: none; }
.bp-doc--design .bp-conditional--false .bp-conditional__badge { background: rgba(199,125,10,0.14); color: #8a5a06; }
.bp-doc--design .bp-conditional--false > .bp-stack { opacity: 0.45; }
.bp-doc--design .bp-repeater { border-left: 2px dashed rgba(79,111,165,0.35); padding-left: 8px; }
.bp-doc--design .bp-conditional { border-left: 2px dashed rgba(199,125,10,0.45); padding-left: 8px; }
.bp-doc--design .bp-image-placeholder { display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #f1f3f6, #e6e9ee); color: #6b7280; font-size: 11px; font-family: Inter, system-ui, sans-serif; border: 1px dashed #cfd5dd; }
.bp-doc--design .bp-logo-placeholder { display: inline-flex; align-items: center; padding: 0 12px; border: 1px dashed #cfd5dd; border-radius: 4px; color: #6b7280; font-size: 11px; font-family: Inter, system-ui, sans-serif; }
.bp-doc--design .bp-node--locked { outline: 1px dashed rgba(0,0,0,0.15); }

@media print {
  .bp-page { break-after: page; page-break-after: always; box-shadow: none !important; margin: 0 !important; }
  .bp-page:last-child { break-after: auto; page-break-after: auto; }
}
`;

/** Builds @font-face rules for a theme's fonts. */
export function fontFaceCss(theme: Theme): string {
  return (theme.fonts ?? [])
    .map((f) => {
      const src = (Array.isArray(f.src) ? f.src : [f.src]).map((u) => `url("${u.replace(/"/g, "%22")}")`).join(", ");
      return `@font-face{font-family:"${f.family.replace(/"/g, "")}";src:${src};font-weight:${f.weight ?? 400};font-style:${f.style ?? "normal"};font-display:${f.display ?? "block"};}`;
    })
    .join("\n");
}
